import {
  Box,
  Button,
  OutlinedInput,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import React, { forwardRef, useEffect, useState } from "react";
import folioDeVenta from "../FolioDeVenta.module.css";
import logoHagalo from "../../../../assets/img/logo-hagalo.jpg";
import logoMercadoLibre from "../../../../assets/img/logo-mercadolibre.png";
import logoCoppel from "../../../../assets/img/logo-coppel.png";
import {
  UPDATE_FOLIO_STEP,
  useStepsContext,
} from "../../../../contexts/steps-controller";
import Theme from "../../../../constants/Theme";
import { searchByOrderAndAmmount } from "../../../../controllers/order";
import { folioValidationSchema } from "../../../../models/yupModels";

const FormFolioDeVenta = forwardRef(function FormFolioDeVenta(
  { nextStep, showToast, setIsError, setDatosCompra, isActive, ...props },
  ref
) {
  const { formStepsState, formStepsController } = useStepsContext();
  const [helperTextFolio, setHelperTextFolio] = useState("Ingresa tu número de folio");
  const [helperTextMonto, setHelperTextMonto] = useState("Ingresa el monto de tu compra");
  const [isDisabled, setIsDisabled] = useState(false);

  const formik = useFormik({
    initialValues: {
      ...formStepsState.folioStep,
      origen: formStepsState.folioStep.origen || "ML",
    },
    validationSchema: folioValidationSchema,
    onSubmit: (props) => {
      setIsDisabled(true);
      searchByOrderAndAmmount(props.folio, props.monto, props.origen)
        .then((result) => {
          formStepsController({
            type: UPDATE_FOLIO_STEP,
            props,
          });
          setDatosCompra(result);
          setTimeout(() => {
            nextStep();
          }, 50);
        })
        .catch((err) => {
          console.log(err, "ERROR");
          setIsError();
          let message = "Ocurrió un error inesperado";
          if (err) message = err;
          showToast({
            type: "error",
            message: message,
          });
        })
        .finally(() => {
          setIsDisabled(false);
        });
    },
  });

  useEffect(() => {
    if (isActive === 1) {
      formik.resetForm();
    }
  }, [isActive]);

  // 🆕 Determinar logo según origen seleccionado (solo ML o Coppel)
  const currentLogo =
    formik.values.origen === "Coppel" ? logoCoppel : logoMercadoLibre;

  const currentLogoAlt =
    formik.values.origen === "Coppel" ? "Logo Coppel" : "Logo Mercado Libre";

  return (
    <Box sx={{ display: "flex" }} ref={ref}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* LOGO SUPERIOR */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 1,
            width: "100%",
          }}
        >
          <img src={logoHagalo} height="60" alt="Logo Hágalo" />
        </Box>

        {/* FORM */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: 1,
            borderRadius: 1,
          }}
        >
          <h2>Ingresa el origen, folio y monto de tu compra para continuar</h2>

          <Box
            sx={{
              flexDirection: "row",
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {/* LOGO DE ORIGEN */}
            <Box
              className={folioDeVenta.sourceContainer}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignSelf: "center",
                justifyContent: "center",
                alignItems: "center",
                p: 3,
                width: 150, // ancho fijo
                minHeight: 150, // altura fija para evitar salto visual
              }}
            >
              <h3 style={{ fontSize: "16px", margin: "5px", textAlign: "center" }}>
                Comprado en:
              </h3>
              <Box
                sx={{
                  width: 140, // el mismo ancho para ambos logos
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  src={currentLogo}
                  style={{ maxWidth: "100%", height: "auto" }}
                  alt={currentLogoAlt}
                />
              </Box>
            </Box>

            {/* CAMPOS */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flex: "1 0",
                flexDirection: "column",
                borderRadius: 1,
              }}
            >
              {/* 🆕 ORIGEN */}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="origen-label">Origen</InputLabel>
                <Select
                  labelId="origen-label"
                  value={formik.values.origen}
                  onChange={formik.handleChange("origen")}
                  onBlur={formik.handleBlur("origen")}
                  label="Origen"
                  error={formik.errors.origen && formik.touched.origen}
                >
                  <MenuItem value="">
                    <em>Selecciona una opción</em>
                  </MenuItem>
                  <MenuItem value="ML">Mercado Libre</MenuItem>
                  <MenuItem value="Coppel">Coppel Marketplace</MenuItem>
                </Select>
                <p
                  style={
                    formik.errors.origen && formik.touched.origen
                      ? { color: Theme.COLORS.ERROR }
                      : {}
                  }
                  className="helperText"
                >
                  {formik.errors.origen && formik.touched.origen
                    ? formik.errors.origen
                    : "Selecciona el origen de tu compra"}
                </p>
              </FormControl>

              {/* FOLIO */}
              <OutlinedInput
                placeholder="Folio"
                onFocus={() => setHelperTextFolio("Asegúrate que sea numérico")}
                sx={{ mt: 2 }}
                onChange={formik.handleChange("folio")}
                value={formik.values.folio}
                error={formik.errors.folio && formik.touched.folio}
                autoComplete="new-password"
              />
              <p
                style={
                  formik.errors.folio && formik.touched.folio
                    ? { color: Theme.COLORS.ERROR }
                    : {}
                }
                className="helperText"
              >
                {formik.errors.folio && formik.touched.folio
                  ? formik.errors.folio
                  : helperTextFolio}
              </p>

              {/* MONTO */}
              <OutlinedInput
                placeholder="Monto"
                onFocus={() => setHelperTextMonto("Asegúrate que sea numérico")}
                sx={{ mt: 2 }}
                onChange={formik.handleChange("monto")}
                value={formik.values.monto}
                error={
                  (formik.errors.monto && formik.touched.monto) ||
                  (formik.errors.montoOrRFC && formik.touched.monto)
                }
                autoComplete="new-password"
              />
              <p
                style={
                  (formik.errors.monto && formik.touched.monto) ||
                  (formik.errors.montoOrRFC && formik.touched.monto)
                    ? { color: Theme.COLORS.ERROR }
                    : {}
                }
                className="helperText"
              >
                {formik.errors.monto && formik.touched.monto
                  ? formik.errors.monto
                  : formik.errors.montoOrRFC && formik.touched.RFC
                  ? formik.errors.montoOrRFC
                  : helperTextMonto}
              </p>
            </Box>
          </Box>
        </Box>

        {/* BOTÓN */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            p: 1,
            my: 2,
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            disabled={isDisabled}
            onClick={() => formik.handleSubmit()}
            sx={{
              backgroundColor: !isDisabled
                ? Theme.COLORS.PRIMARY
                : Theme.COLORS.LIGHT_GRAY,
              fontFamily: "Montserrat, Arial, sans-serif",
            }}
          >
            {!isDisabled ? "Siguiente paso" : "Cargando"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
});

export default FormFolioDeVenta;
